import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";
import { CustomSelect } from "../components/CustomSelect";
import type { CustomSelectOption } from "../components/CustomSelect";
import { ApiError, tenants as tenantsApi } from "../lib/api";
import type {
  NivelTenant,
  Tenant,
  TenantCreateBody,
  TenantStatus,
} from "../lib/api";

const NIVEL_OPTS: ReadonlyArray<CustomSelectOption<NivelTenant>> = [
  { value: "nacional", label: "Gobierno Nacional" },
  { value: "regional", label: "Regional" },
  { value: "gobernacion", label: "Gobernación" },
  { value: "municipio", label: "Municipio / Alcaldía" },
  { value: "jac", label: "Junta de Acción Comunal" },
];

const STATUS_LABEL: Record<TenantStatus, string> = {
  trial: "Trial",
  active: "Activa",
  suspended: "Suspendida",
  cancelled: "Cancelada",
};

const STATUS_COLOR: Record<TenantStatus, string> = {
  trial: "var(--warn)",
  active: "var(--ok)",
  suspended: "var(--err)",
  cancelled: "var(--ink-4)",
};

const STATUS_OPTS: ReadonlyArray<CustomSelectOption<TenantStatus | "">> = [
  { value: "", label: "Todos los estados" },
  { value: "trial", label: "Trial" },
  { value: "active", label: "Activa" },
  { value: "suspended", label: "Suspendida" },
  { value: "cancelled", label: "Cancelada" },
];

export function TenantsPage() {
  const [items, setItems] = useState<Tenant[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<TenantStatus | "">("");
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await tenantsApi.list({
        q: q || undefined,
        status: statusFilter || undefined,
      });
      setItems(resp.items);
      setTotal(resp.total);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "No se pudo cargar la lista.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    void load();
  };

  return (
    <Shell>
      <section className="page">
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 24,
            flexWrap: "wrap",
            marginBottom: 28,
          }}
        >
          <div>
            <div className="eyebrow">Plataforma · Tenants</div>
            <h1>Alcaldías</h1>
            <p className="lede" style={{ marginBottom: 0 }}>
              {total === 0
                ? "Aún no hay alcaldías registradas."
                : `${total} alcaldía${total === 1 ? "" : "s"} registrada${total === 1 ? "" : "s"}.`}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Link
              to="/onboarding"
              className="cta"
              style={{ maxWidth: 220, textDecoration: "none" }}
            >
              <span className="cta-inner">+ Nueva alcaldía (wizard)</span>
            </Link>
            <button
              className="btn-outline"
              type="button"
              onClick={() => setShowCreate(true)}
            >
              Form rápido
            </button>
          </div>
        </div>

        <form
          onSubmit={onSearch}
          style={{
            display: "flex",
            gap: 12,
            marginBottom: 24,
            flexWrap: "wrap",
          }}
        >
          <input
            className="input"
            type="text"
            placeholder="Buscar por nombre, municipio o NIT"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ flex: 1, minWidth: 240, maxWidth: 480 }}
          />
          <div style={{ maxWidth: 180 }}>
            <CustomSelect<TenantStatus | "">
              value={statusFilter}
              onChange={(v) => setStatusFilter(v)}
              options={STATUS_OPTS}
              aria-label="Filtrar por estado"
            />
          </div>
          <button type="submit" className="logout-btn">
            Filtrar
          </button>
        </form>

        {error && <div className="alert err">{error}</div>}

        <div className="card">
          <div className="card-inner" style={{ padding: 0 }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr style={{ borderBottom: "1px solid var(--hairline-strong)" }}>
                  <Th>Nombre</Th>
                  <Th>NIT</Th>
                  <Th>Departamento</Th>
                  <Th>Estado</Th>
                  <Th>Creada</Th>
                  <Th>Abrir</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ padding: 40, textAlign: "center", color: "var(--ink-3)" }}
                    >
                      Cargando…
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ padding: 40, textAlign: "center", color: "var(--ink-3)" }}
                    >
                      Sin resultados.
                    </td>
                  </tr>
                ) : (
                  items.map((t) => (
                    <tr
                      key={t.id}
                      style={{ borderBottom: "1px solid var(--hairline)" }}
                    >
                      <Td>
                        <div style={{ fontWeight: 600 }}>{t.nombre}</div>
                        <div
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: 11,
                            color: "var(--ink-3)",
                          }}
                        >
                          {t.slug} · {t.municipio}
                        </div>
                      </Td>
                      <Td mono>{t.nit}</Td>
                      <Td>{t.departamento}</Td>
                      <Td>
                        <span
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: 11,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            color: STATUS_COLOR[t.status],
                          }}
                        >
                          {STATUS_LABEL[t.status]}
                        </span>
                      </Td>
                      <Td mono>{new Date(t.created_at).toLocaleDateString("es-CO")}</Td>
                      <Td>
                        <Link
                          to={`/tenants/${t.id}`}
                          className="tenant-row-open"
                          title="Abrir vista de la alcaldía"
                          aria-label={`Abrir ${t.nombre}`}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="13 6 19 12 13 18" />
                          </svg>
                        </Link>
                      </Td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {showCreate && (
        <CreateTenantModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void load();
          }}
        />
      )}
    </Shell>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: "16px 22px",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: "var(--ink-3)",
        fontWeight: 500,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  mono,
}: {
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <td
      style={{
        padding: "14px 22px",
        fontFamily: mono ? "var(--font-mono)" : "var(--font-app)",
        fontSize: mono ? 13 : 14,
      }}
    >
      {children}
    </td>
  );
}

// Mirror del backend domain/tenants.py:slugify
function frontSlugify(nombre: string): string {
  const stripped = nombre
    .replace(/^alcald[íi]a\s+(de la|del|de)\s+/i, "")
    .trim()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "");
  return stripped
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{1,62}[a-z0-9])?$/;

function CreateTenantModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [form, setForm] = useState<TenantCreateBody>({
    nombre: "",
    nit: "",
    departamento: "",
    municipio: "",
    email_contacto: "",
    slug: "",
    nivel: "municipio",
  });
  const [slugDirty, setSlugDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    tenantsApi
      .list({})
      .then((r) => setTenants(r.items))
      .catch(() => setTenants([]));
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await tenantsApi.create(form);
      onCreated();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : err.message || "No se pudo crear la alcaldía.",
        );
      } else {
        setError("Error de red.");
      }
    } finally {
      setBusy(false);
    }
  };

  const set = <K extends keyof TenantCreateBody>(
    key: K,
    value: TenantCreateBody[K],
  ) => setForm((f) => ({ ...f, [key]: value }));

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,22,26,0.55)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "60px 20px",
        zIndex: 50,
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ maxWidth: 620, width: "100%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-inner">
          <div className="eyebrow">Plataforma · Alta</div>
          <h2 style={{ marginTop: 4 }}>Nueva alcaldía</h2>
          <p style={{ color: "var(--ink-3)", marginBottom: 22 }}>
            La alcaldía inicia en estado <code>trial</code>. Podrás activarla
            cuando confirmen el contrato.
          </p>

          {error && <div className="alert err">{error}</div>}

          <form onSubmit={submit}>
            <div className="form-row">
              <label className="label">Nombre oficial</label>
              <input
                className="input"
                value={form.nombre}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => ({
                    ...f,
                    nombre: v,
                    slug: slugDirty ? f.slug : frontSlugify(v),
                  }));
                }}
                placeholder="Alcaldía de Bucaramanga"
                required
                minLength={3}
              />
            </div>

            <div className="form-row">
              <label className="label">Subdominio (URL del tenant)</label>
              <div style={{ display: "flex", alignItems: "stretch" }}>
                <input
                  className="input"
                  value={form.slug ?? ""}
                  onChange={(e) => {
                    setSlugDirty(true);
                    set(
                      "slug",
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9-]/g, "")
                        .slice(0, 64),
                    );
                  }}
                  placeholder="bucaramanga"
                  pattern="[a-z0-9]([a-z0-9-]{1,62}[a-z0-9])?"
                  minLength={3}
                  maxLength={64}
                  required
                  style={{
                    fontFamily: "var(--font-mono)",
                    flex: "0 0 240px",
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  }}
                />
                <div
                  style={{
                    background: "var(--bone-warm)",
                    border: "1px solid var(--hairline-strong)",
                    borderLeft: "none",
                    padding: "12px 14px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 14,
                    color: "var(--ink-3)",
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  .mipuebloapp.com
                </div>
              </div>
              <p className="helper">
                {form.slug && SLUG_RE.test(form.slug) ? (
                  <>
                    URL del portal ciudadano:{" "}
                    <code style={{ fontFamily: "var(--font-mono)" }}>
                      https://{form.slug}.mipuebloapp.com
                    </code>
                  </>
                ) : (
                  <>
                    Solo minúsculas, dígitos y guiones. 3-64 caracteres, sin guion al inicio o final.
                  </>
                )}
              </p>
            </div>

            <div
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}
            >
              <div className="form-row">
                <label className="label">NIT</label>
                <input
                  className="input"
                  value={form.nit}
                  onChange={(e) => set("nit", e.target.value)}
                  placeholder="890201813-9"
                  required
                />
              </div>
              <div className="form-row">
                <label className="label">Código DIVIPOLA</label>
                <input
                  className="input"
                  value={form.codigo_divipola ?? ""}
                  onChange={(e) =>
                    set("codigo_divipola", e.target.value || undefined)
                  }
                  placeholder="68001"
                  maxLength={8}
                />
              </div>
            </div>

            <div
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}
            >
              <div className="form-row">
                <label className="label">Departamento</label>
                <input
                  className="input"
                  value={form.departamento}
                  onChange={(e) => set("departamento", e.target.value)}
                  required
                  minLength={2}
                />
              </div>
              <div className="form-row">
                <label className="label">Municipio</label>
                <input
                  className="input"
                  value={form.municipio}
                  onChange={(e) => set("municipio", e.target.value)}
                  required
                  minLength={2}
                />
              </div>
            </div>

            <div className="form-row">
              <label className="label">Email institucional</label>
              <input
                className="input"
                type="email"
                value={form.email_contacto}
                onChange={(e) => set("email_contacto", e.target.value)}
                placeholder="contacto@alcaldia.gov.co"
                required
              />
            </div>

            <div className="form-row">
              <label className="label">Teléfono</label>
              <input
                className="input"
                value={form.telefono ?? ""}
                onChange={(e) => set("telefono", e.target.value || undefined)}
                placeholder="+57 ..."
              />
            </div>

            <div className="form-row">
              <label className="label">Nivel jerárquico</label>
              <CustomSelect<NivelTenant>
                value={form.nivel ?? "municipio"}
                onChange={(v) => set("nivel", v)}
                options={NIVEL_OPTS}
                aria-label="Nivel jerárquico"
              />
            </div>

            <div className="form-row">
              <label className="label">Entidad superior (padre)</label>
              <CustomSelect
                value={form.parent_id ?? ""}
                onChange={(v) => set("parent_id", v || null)}
                options={[
                  { value: "", label: "— Sin padre (raíz) —" },
                  ...tenants.map((t) => ({ value: t.id, label: t.nombre })),
                ]}
                aria-label="Entidad superior"
              />
              <p className="helper">
                Para una gobernación/regional/nación: elige su nivel arriba y su padre aquí.
                Una alcaldía normalmente cuelga de su gobernación.
              </p>
            </div>

            <div
              style={{
                display: "flex",
                gap: 12,
                justifyContent: "flex-end",
                marginTop: 20,
              }}
            >
              <button
                type="button"
                className="logout-btn"
                onClick={onClose}
                disabled={busy}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="cta"
                disabled={busy}
                style={{ maxWidth: 220 }}
              >
                <span className="cta-inner">
                  {busy && <span className="spinner" aria-hidden />}
                  Crear alcaldía
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
